import { headers_custom_func_get, headers_file_func_get, headers_guidFile_func_get } from "../Auth/get_set_AuthHeaders";
import {apiUriHttp, apiUriExcelHttp} from "../conf/apiConf";


function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function sendAllDataPost_byApiNameAndData(apiName: string, DataToSend: any, baseUri: string = apiUriHttp()): Promise<any> {
  //console.log(apiName, ' called with ', DataToSend );
  try {
    const response = await fetch(baseUri + apiName, {
      method: 'POST',
      headers: headers_custom_func_get(),
      body: JSON.stringify(DataToSend),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('UNAUTHORIZED');
      }
      throw new Error(`Failed to fetch data for ${apiName}`);
    }
    else{
      const data: any = await response.json();
      //console.log(data)
      return data.responseData;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function sendAllExcelExtract(apiName: string, DataToSend: any): Promise<any> {
  //console.log(apiName, ' called with ', DataToSend );
  try {
    const response = await fetch(apiUriExcelHttp() + apiName, {
      method: 'POST',
      headers: headers_custom_func_get(),
      body: JSON.stringify(DataToSend),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('UNAUTHORIZED');
      }
      throw new Error(`Failed to fetch data for ${apiName}`);
    }
    else{
      const data: any = await response.json();
      return data.responseData;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export interface ExtractProgressEvent {
  stage: string;
  message: string;
  current: number | null;
  total: number | null;
}

// Streaming counterpart to sendAllExcelExtract: same request, but the server holds the connection
// open and sends "event: progress" frames as it works (still a plain POST + streamed response
// body, not the browser's native EventSource, which only supports GET and can't carry the custom
// auth headers this API needs) - onProgress is called for each one, and the returned promise
// resolves with the final { success, message } once the server sends its "done" frame (or rejects
// on an "error" frame / network failure).
async function sendAllExcelExtractStream(
  apiName: string,
  DataToSend: any,
  onProgress: (event: ExtractProgressEvent) => void,
  signal?: AbortSignal,
): Promise<any> {
  const response = await fetch(apiUriExcelHttp() + apiName, {
    method: 'POST',
    headers: headers_custom_func_get(),
    body: JSON.stringify(DataToSend),
    signal,
  });

  if (!response.ok || !response.body) {
    if (response.status === 401) {
      console.log('UNAUTHORIZED');
    }
    throw new Error(`Failed to fetch data for ${apiName}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // SSE frames are separated by a blank line; each frame has "event: <name>" and "data: <json>"
  // lines. Parsed by hand here since this is a plain fetch() stream, not EventSource.
  const parseFrame = (rawFrame: string): { eventName: string; data: any } | null => {
    let eventName = 'message';
    let dataText = '';
    for (const line of rawFrame.split('\n')) {
      if (line.startsWith('event:')) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataText += line.slice('data:'.length).trim();
      }
    }
    if (!dataText) {
      return null;
    }
    try {
      return { eventName, data: JSON.parse(dataText) };
    } catch {
      return null;
    }
  };

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });

    let frameEnd = buffer.indexOf('\n\n');
    while (frameEnd !== -1) {
      const rawFrame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);

      const frame = parseFrame(rawFrame);
      if (frame) {
        if (frame.eventName === 'progress') {
          onProgress(frame.data as ExtractProgressEvent);
        } else if (frame.eventName === 'done') {
          return frame.data;
        } else if (frame.eventName === 'error') {
          throw new Error(frame.data?.message || 'Extraction failed');
        }
      }

      frameEnd = buffer.indexOf('\n\n');
    }
  }

  throw new Error('Connection closed before the server reported a final result.');
}

async function getFileByGuid_Post(apiName: string, DataToSend: any, baseUri: string = apiUriExcelHttp()): Promise<string> {
  try {
    const response = await fetch(baseUri + apiName, {
      method: 'POST',
      headers: headers_guidFile_func_get(),
      body: JSON.stringify(DataToSend),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('UNAUTHORIZED');
      }
      throw new Error(`Failed to fetch data for ${apiName}`);
    }

    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}

async function pollForFile(apiName, DataToSend, interval = 2500, maxAttempts = 2, baseUri: string = apiUriExcelHttp()) {
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const response = await fetch(baseUri + apiName, {
        method: 'POST',
        headers: headers_guidFile_func_get(),
        body: JSON.stringify(DataToSend),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('UNAUTHORIZED');
        }
        throw new Error(`Failed to fetch data for ${apiName}`);
      }

      const blob = await response.blob();

      // Check if the blob is not empty
      if (blob.size > 0) {
        return blob;
      }

    } catch (error) {
      console.log(error);
      if (attempts === maxAttempts - 1) {
        throw new Error('Max attempts reached, unable to fetch final response');
      }
    }

    // Wait before the next attempt
    await delay(interval);
    attempts++;
  }
}


async function getFileOfPdfChangeover_Post(apiName: string, DataToSend: any, baseUri: string = apiUriHttp()): Promise<string> {
  try {
    const response = await fetch(baseUri + apiName, {
      method: 'POST',
      mode: 'cors',
      headers: headers_custom_func_get(),
      body: JSON.stringify(DataToSend),
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('UNAUTHORIZED');
      }
      throw new Error(`Failed to fetch data for ${apiName}`);
    }

    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onloadend = () => {
        resolve(reader.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
}


async function sendAllFileByPost(apiName: string, DataToSend: any, signal?: AbortSignal, baseUri: string = apiUriExcelHttp()): Promise<any> {
  try {
    const customHeaders = headers_file_func_get();

    const response = await fetch(baseUri + apiName, {
      method: 'POST',
      headers: customHeaders,
      body: DataToSend,
      signal,
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.log('UNAUTHORIZED');
      }
      throw new Error(`Failed to fetch data for ${apiName}`);
    }
    else{
      const data: any = await response.json();
      return data;
    }
  } catch (error) {
    console.log(error);
    throw error;
  }
}

export {
  sendAllDataPost_byApiNameAndData,
  sendAllFileByPost,
  sendAllExcelExtract,
  sendAllExcelExtractStream,
  getFileByGuid_Post,
  pollForFile,
  getFileOfPdfChangeover_Post
}
